import { DefaultRoles, Permissions } from '@api/apiClient';
import { jwtDecode } from 'jwt-decode';
import { toJS } from 'mobx';

// Note: ProjectSustainabilityAdmin is defined in backend but may not yet be in auto-generated apiClient.
// Once apiClient is regenerated, use DefaultRoles.ProjectSustainabilityAdmin instead.
const ROLE_PROJECT_SUSTAINABILITY_ADMIN = 'ProjectSustainabilityAdmin';

const PROJECT_SETUP_ADMIN_MODEL_PERMISSIONS = [
  Permissions.App_Project_Model_Create,
  Permissions.App_Project_Model_View,
];
// Add to the global window object for auto-completion in the console
// > window.UserPermissionStore
export { };
declare global {
  interface Window {
    UserPermissionStore: UserPermissionStore;
  }
}

interface JwtPayload {
  email: string;
  role: string | string[] | undefined;
  permission: string | string[] | undefined;
  expiresIn: number;
  nbf: number;
  exp: number;
  iat: number;
}

interface UserPermission {
  email: string;
  roles: string[];
  permissions: string[];
  expiresIn: number;
}

class UserPermissionStore {
  public userPermission: UserPermission | null = null;
  public primaryRole: string | undefined = undefined;
  private resourcePermission: string[] = []; // Store the app store instance

  constructor() {
    window.UserPermissionStore = this;
    //makeAutoObservable(this, {}, { autoBind: true });
  };

  get dump() {
    return toJS(this);
  }

  setPermission = (jwtToken: string) => {
    if (jwtToken) {
      const decodedToken = jwtDecode<JwtPayload>(jwtToken);
      this.userPermission = {
        email: decodedToken?.email,
        roles: this.toArray(decodedToken?.role),
        permissions: this.toArray(decodedToken?.permission),
        expiresIn: decodedToken?.expiresIn || 0,
      };

      this.primaryRole = this.userPermission?.roles[0];
    }
    else {
      this.userPermission = null;
    }
  };

  toArray = (input: string | string[] | undefined) => {
    if (Array.isArray(input)) {
      return input;
    } else if (input) {
      return [input];
    }
    return [];
  };

  hasPermissionForDashBoard = (permission: string, projectPermissions: string[] = []) => {
    // check if user is admin 
    const hasManagePermission = this.userPermission?.permissions.some(p => p.includes(Permissions.App_Manage.toLowerCase()));
    if (hasManagePermission) {
      return hasManagePermission;
    }

    // check if user has manage permission in project
    const hasProjectManagePermission = projectPermissions.some(p => p.includes(Permissions.App_Manage.toLowerCase()));
    if (hasProjectManagePermission) {
      return hasProjectManagePermission;
    }

    const hasSpecificPermission = projectPermissions.some(p => p.toLowerCase() === permission?.toLowerCase());

    return hasSpecificPermission;
  };

  hasPermission = (permission: string) => {
    // check if user has ProjectSustainabilityAdmin role - grant all permissions
    if (this.hasRole(ROLE_PROJECT_SUSTAINABILITY_ADMIN)) {
      return true;
    }

    if (
      this.hasRole(DefaultRoles.ProjectSetupAdmin) &&
      PROJECT_SETUP_ADMIN_MODEL_PERMISSIONS.some(p => p.toLowerCase() === permission?.toLowerCase())
    ) {
      return true;
    }

    const hasManagePermission = this.userPermission?.permissions.some(p => p.includes(Permissions.App_Manage.toLowerCase()));
    if (hasManagePermission) {
      return hasManagePermission;
    }

    // check if user has manage permission in project
    const hasProjectManagePermission = this.resourcePermission.some(p => p.includes(Permissions.App_Manage.toLowerCase()));
    if (hasProjectManagePermission) {
      return hasProjectManagePermission;
    }

    // Check if user has the specific permission in their global permissions (from JWT token)
    const hasUserPermission = this.userPermission?.permissions.some(p => p.toLowerCase() === permission?.toLowerCase());
    if (hasUserPermission) {
      return hasUserPermission;
    }

    // Check if user has the specific permission in their resource/project permissions
    const hasSpecificPermission = this.resourcePermission.some(p => p.toLowerCase() === permission?.toLowerCase());

    return hasSpecificPermission;

  };

  setResourcePermissions = (permissions?: string[]) => {
    this.resourcePermission = permissions ?? [];
  };

  hasRole = (roleName: string): boolean => {
    const roles = this.userPermission?.roles || [];
    return roles.some(r => r.toLowerCase() === roleName.toLowerCase());
  };

  isProjectSustainabilityAdmin = (): boolean => {
    return this.hasRole(ROLE_PROJECT_SUSTAINABILITY_ADMIN);
  };

  canAccessAppSettings = (): boolean => {
    return this.hasPermission(Permissions.App_Manage) || this.hasRole(DefaultRoles.IctSystemAdmin);
  };
}

export default UserPermissionStore;
